import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  Envelope, 
  Calendar, 
  Bank, 
  ShieldCheck,
  Lock,
  Key,
  ClockCounterClockwise,
  Fingerprint,
  CheckCircle,
  Warning,
  ArrowSquareOut,
  Gear,
  GoogleLogo,
  WhatsappLogo,
  X,
  SpinnerGap
} from '@phosphor-icons/react';
import { NotificationBanner } from '../components/dashboard';
import GmailFiltersPanel from '../components/connections/GmailFiltersPanel';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

export default function Connections() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [gmailStatus, setGmailStatus] = useState({ connected: false, connected_at: null });
  const [calendarStatus, setCalendarStatus] = useState({ connected: false });
  const [waStatus, setWaStatus] = useState({ connected: false, status: 'unknown' });
  const [showWaQr, setShowWaQr] = useState(false);
  const [waQr, setWaQr] = useState(null);
  const [waPolling, setWaPolling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  // Auto-dismiss the scan result banner after 15s so it doesn't linger across
  // subsequent user actions. User can also click the X to dismiss early.
  useEffect(() => {
    if (!scanResult) return;
    const t = setTimeout(() => setScanResult(null), 15000);
    return () => clearTimeout(t);
  }, [scanResult]);
  const [autoScan, setAutoScan] = useState({ enabled: false, frequency: '1hr', last_scan_at: null });
  const [savingAutoScan, setSavingAutoScan] = useState(false);
  const [scanPrefs, setScanPrefs] = useState({ gmail_limit: 100, whatsapp_limit: 100 });
  const [savingScanPrefs, setSavingScanPrefs] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [imageStyle, setImageStyle] = useState({ prefs: {}, person_appearance: '', options: { aesthetics: [], settings: [], person_styles: [] } });
  const [savingImageStyle, setSavingImageStyle] = useState(false);

  useEffect(() => {
    if (searchParams.get('google') === 'connected' || searchParams.get('gmail') === 'connected') {
      setScanResult({ success: true, message: 'Google connected successfully! Gmail & Calendar are now active.' });
    }
    if (searchParams.get('google') === 'error') {
      const reason = searchParams.get('reason');
      setScanResult({ success: false, message: reason === 'expired' 
        ? 'Gmail connection expired. Please try connecting again.' 
        : 'Gmail connection failed. Please try again.' });
    }
    fetchGmailStatus();
    fetchCalendarStatus();
    fetchAutoScanSettings();
    fetchScanPrefs();
    fetchImageStyle();
    fetchWaStatus();
  }, [searchParams]);

  const fetchScanPrefs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gmail/scan-prefs`, { withCredentials: true });
      setScanPrefs({ gmail_limit: res.data.gmail_limit, whatsapp_limit: res.data.whatsapp_limit });
    } catch { /* keep defaults */ }
  };

  const fetchImageStyle = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/image-style-prefs`, { withCredentials: true });
      setImageStyle({
        prefs: res.data.prefs || {},
        person_appearance: res.data.person_appearance || '',
        options: res.data.options || { aesthetics: [], settings: [], person_styles: [] },
      });
    } catch { /* keep defaults */ }
  };

  const saveImageStyle = async (patch) => {
    setSavingImageStyle(true);
    try {
      const nextPrefs = { ...imageStyle.prefs, ...patch.prefs };
      const res = await axios.post(`${API_URL}/api/dashboard/image-style-prefs`, {
        ...nextPrefs,
        person_appearance: patch.person_appearance ?? imageStyle.person_appearance,
      }, { withCredentials: true });
      setImageStyle(prev => ({ ...prev, prefs: res.data.prefs, person_appearance: res.data.person_appearance || prev.person_appearance }));
    } catch {
      /* swallow */
    } finally {
      setSavingImageStyle(false);
    }
  };

  const saveScanPrefs = async (next) => {
    setSavingScanPrefs(true);
    try {
      const res = await axios.post(`${API_URL}/api/gmail/scan-prefs`, next, { withCredentials: true });
      setScanPrefs({ gmail_limit: res.data.gmail_limit, whatsapp_limit: res.data.whatsapp_limit });
    } catch (e) {
      setScanResult({ success: false, message: 'Could not save scan preferences' });
    } finally {
      setSavingScanPrefs(false);
    }
  };

  const fetchWaStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/whatsapp/status`, { withCredentials: true });
      setWaStatus({ connected: res.data.connected, status: res.data.status });
    } catch {
      setWaStatus({ connected: false, status: 'unavailable' });
    }
  };

  const handleConnectWhatsApp = () => {
    setShowWaQr(true);
    setWaPolling(true);
    pollWhatsAppQr();
  };

  const pollWhatsAppQr = async () => {
    let attempts = 0;
    const poll = async () => {
      if (attempts > 60) { setWaPolling(false); return; }
      attempts++;
      try {
        const res = await axios.get(`${API_URL}/api/whatsapp/qr`, { withCredentials: true });
        if (res.data.connected) {
          setWaStatus({ connected: true, status: 'connected' });
          setShowWaQr(false);
          setWaPolling(false);
          setScanResult({ success: true, message: 'WhatsApp connected successfully!' });
          return;
        }
        if (res.data.qr) setWaQr(res.data.qr);
      } catch { /* continue */ }
      setTimeout(poll, 3000);
    };
    poll();
  };

  const handleDisconnectWhatsApp = async () => {
    if (disconnectConfirm !== 'whatsapp') {
      setDisconnectConfirm('whatsapp');
      return;
    }
    setDisconnectConfirm(null);
    try {
      await axios.post(`${API_URL}/api/whatsapp/disconnect`, {}, { withCredentials: true });
      setWaStatus({ connected: false, status: 'disconnected' });
      setScanResult({ success: true, message: 'WhatsApp disconnected.' });
    } catch { /* ignore */ }
  };

  const fetchGmailStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gmail/status`, { withCredentials: true });
      setGmailStatus(response.data);
    } catch (error) {
      console.error('Gmail status error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/calendar/status`, { withCredentials: true });
      setCalendarStatus(response.data);
    } catch (error) {
      console.error('Calendar status error:', error);
    }
  };

  const fetchAutoScanSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auto-scan/settings`, { withCredentials: true });
      setAutoScan(res.data);
    } catch (error) {
      console.error('Auto-scan settings error:', error);
    }
  };

  const handleToggleAutoScan = async (enabled) => {
    setSavingAutoScan(true);
    try {
      const res = await axios.post(`${API_URL}/api/auto-scan/settings`, {
        enabled,
        frequency: autoScan.frequency
      }, { withCredentials: true });
      setAutoScan(prev => ({ ...prev, enabled: res.data.enabled, frequency: res.data.frequency }));
      setScanResult({ success: true, message: enabled ? `Auto-scanning enabled (${res.data.frequency_label})` : 'Auto-scanning disabled' });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to update auto-scan';
      setScanResult({ success: false, message: detail });
    } finally {
      setSavingAutoScan(false);
    }
  };

  const handleChangeFrequency = async (frequency) => {
    setAutoScan(prev => ({ ...prev, frequency }));
    if (autoScan.enabled) {
      setSavingAutoScan(true);
      try {
        const res = await axios.post(`${API_URL}/api/auto-scan/settings`, {
          enabled: true,
          frequency
        }, { withCredentials: true });
        setAutoScan(prev => ({ ...prev, frequency: res.data.frequency }));
        setScanResult({ success: true, message: `Scan frequency updated to ${res.data.frequency_label}` });
      } catch (error) {
        console.error('Frequency update error:', error);
      } finally {
        setSavingAutoScan(false);
      }
    }
  };

  const handleConnectGmail = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/oauth/gmail/login`, { withCredentials: true });
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Gmail connect error:', error);
      alert('Failed to start Gmail connection. Please try again.');
    }
  };

  const [disconnectConfirm, setDisconnectConfirm] = useState(null);

  const handleDisconnectGmail = async () => {
    if (disconnectConfirm !== 'gmail') {
      setDisconnectConfirm('gmail');
      return;
    }
    setDisconnectConfirm(null);
    try {
      await axios.delete(`${API_URL}/api/gmail/disconnect`, { withCredentials: true });
      setGmailStatus({ connected: false, connected_at: null });
      setCalendarStatus({ connected: false });
      // Disable auto-scan if it was on
      if (autoScan.enabled) {
        await axios.post(`${API_URL}/api/auto-scan/settings`, { enabled: false, frequency: autoScan.frequency }, { withCredentials: true });
        setAutoScan(prev => ({ ...prev, enabled: false }));
      }
      setScanResult({ success: true, message: 'Google disconnected. Your existing tasks are preserved.' });
    } catch (error) {
      console.error('Gmail disconnect error:', error);
      setScanResult({ success: false, message: 'Failed to disconnect Gmail. Please try again.' });
    }
  };

  const handleConnectCalendar = async () => {
    // Uses the same Google OAuth flow but now includes Calendar scope
    try {
      const response = await axios.get(`${API_URL}/api/oauth/gmail/login`, { withCredentials: true });
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Calendar connect error:', error);
      alert('Failed to start Google Calendar connection. Please try again.');
    }
  };

  const handleScanGmail = async () => {
    setScanning(true);
    setScanResult(null);
    setScanProgress({ current: 0, total: scanPrefs.gmail_limit, stage: 'starting' });
    try {
      // Kick off a background job
      const start = await axios.post(`${API_URL}/api/gmail/scan`, {}, { withCredentials: true });
      const jobId = start.data.job_id;

      // Poll for completion (every 2s — keeps polling as long as user stays on page)
      while (true) {
        await new Promise(r => setTimeout(r, 2000));
        const res = await axios.get(`${API_URL}/api/gmail/scan/status/${jobId}`, { withCredentials: true });
        const { status, progress, result, error } = res.data;
        if (progress) setScanProgress(progress);
        if (status === 'completed') {
          setScanResult({
            success: true,
            message: `Scanned ${result.scanned} emails. ${result.tasks_created} new tasks, ${result.tasks_updated || 0} updated, ${result.tasks_skipped || 0} duplicates skipped.`
          });
          // Scroll the inline banner (above the Gmail card) into view so
          // the user sees the result where they triggered the scan, not at
          // the top of the page.
          setTimeout(() => {
            document.querySelector('[data-testid="connection-banner"]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
          return;
        }
        if (status === 'failed' || status === 'error') {
          setScanResult({ success: false, message: error || 'Failed to scan Gmail' });
          return;
        }
      }
    } catch (error) {
      setScanResult({ success: false, message: error.response?.data?.detail || 'Failed to scan Gmail' });
    } finally {
      setScanning(false);
      setScanProgress(null);
    }
  };

  const connections = [
    {
      id: 'gmail',
      name: 'Gmail',
      description: gmailStatus.connected
        ? 'Synced for bills & appointments'
        : 'Connect to scan for bills, appointments, subscriptions',
      icon: Envelope,
      connected: gmailStatus.connected,
      encrypted: true,
      onConnect: handleConnectGmail,
      onDisconnect: handleDisconnectGmail,
      onScan: gmailStatus.connected ? handleScanGmail : null,
    },
    {
      id: 'gcalendar',
      name: 'Google Calendar',
      description: calendarStatus.connected
        ? 'Syncing tasks to your calendar'
        : gmailStatus.connected
          ? 'One click to enable — uses your existing Google connection'
          : 'Connects automatically when you link Gmail',
      icon: GoogleLogo,
      connected: calendarStatus.connected,
      encrypted: true,
      onConnect: gmailStatus.connected ? handleConnectCalendar : null,
      needsReconnect: gmailStatus.connected && !calendarStatus.connected,
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      description: waStatus.connected
        ? 'Connected — send reminders & summarize chats'
        : 'Connect to send reminders and read chat summaries',
      icon: WhatsappLogo,
      connected: waStatus.connected,
      encrypted: true,
      onConnect: handleConnectWhatsApp,
      onDisconnect: waStatus.connected ? handleDisconnectWhatsApp : null,
    },
    {
      id: 'outlook',
      name: 'Outlook',
      description: 'Calendar & Contact Sync',
      icon: Calendar,
      connected: false,
      readOnly: true,
      comingSoon: true
    },
    {
      id: 'bank',
      name: 'Chase Bank',
      description: 'Transaction monitoring & budgeting',
      icon: Bank,
      connected: false,
      setupRequired: true,
      comingSoon: true
    },
    {
      id: 'calendar',
      name: 'Apple Calendar',
      description: 'Family & personal schedule',
      icon: Calendar,
      connected: false,
      encrypted: true,
      comingSoon: true
    }
  ];

  return (
    <Layout>
      <div className="p-4 lg:p-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="display-lg text-4xl">Connections & Privacy</h1>
          <p className="body-lg mt-3 max-w-2xl">
            Secure your ecosystem. Manage how LifeAdmin AI interacts with your data sources through enterprise-grade encryption and granular permissions.
          </p>
        </div>

        {/* Push Notification Opt-in */}
        <NotificationBanner />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">

            {/* Connection Success Banner moved next to the connections grid */}

            {/* Intelligence Parameters */}
            <div className="surface-card p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#d6e3ff] flex items-center justify-center">
                    <Gear size={20} className="text-[#005bc1]" weight="duotone" />
                  </div>
                  <h2 className="title-md">Intelligence Parameters</h2>
                </div>
                <span className="tag tag-primary">System Level</span>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-5 bg-[#f2f4f6] rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Gear size={18} className="text-[#596065]" />
                      <span className="font-semibold text-[#2d3338]">AI Task Extraction</span>
                    </div>
                    <div className="w-12 h-6 bg-[#005bc1] rounded-full relative">
                      <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow-sm"></div>
                    </div>
                  </div>
                  <p className="text-xs text-[#596065]">Automatically detect tasks and deadlines from connected data.</p>
                </div>

                <div className="p-5 bg-[#f2f4f6] rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <ClockCounterClockwise size={18} className="text-[#596065]" />
                      <span className="font-semibold text-[#2d3338]">Auto-scanning</span>
                    </div>
                    <button
                      onClick={() => handleToggleAutoScan(!autoScan.enabled)}
                      disabled={savingAutoScan || !gmailStatus.connected}
                      className={`w-12 h-6 rounded-full relative transition-colors ${
                        autoScan.enabled ? 'bg-[#005bc1]' : 'bg-[#dce1e6]'
                      } ${(!gmailStatus.connected || savingAutoScan) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid="auto-scan-toggle"
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${
                        autoScan.enabled ? 'right-1' : 'left-1'
                      }`}></div>
                    </button>
                  </div>
                  <p className="text-xs text-[#596065] mb-3">
                    {gmailStatus.connected
                      ? 'Periodically scan your email for new bills, appointments, and subscriptions.'
                      : 'Connect Gmail first to enable auto-scanning.'}
                  </p>
                  {gmailStatus.connected && (
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">Scan Frequency</label>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { value: '15min', label: '15m' },
                          { value: '1hr', label: '1h' },
                          { value: '6hr', label: '6h' },
                          { value: '12hr', label: '12h' },
                          { value: 'daily', label: '24h' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => handleChangeFrequency(opt.value)}
                            disabled={savingAutoScan}
                            className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                              autoScan.frequency === opt.value
                                ? 'bg-[#005bc1] text-white'
                                : 'bg-white text-[#596065] hover:bg-[#ebeef2] border border-[#ebeef2]'
                            }`}
                            data-testid={`freq-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {autoScan.enabled && autoScan.last_scan_at && (
                        <p className="text-[10px] text-[#acb3b8] mt-1">
                          Last scan: {new Date(autoScan.last_scan_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Scan limits — how many emails/messages per scan */}
                <div className="p-5 bg-[#f2f4f6] rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Gear size={18} className="text-[#596065]" />
                    <span className="font-semibold text-[#2d3338]">Scan limits</span>
                  </div>
                  <p className="text-xs text-[#596065] mb-3">
                    How many recent messages to process per scan. Higher = more thorough, but slower.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider flex justify-between">
                        <span>Gmail emails per scan</span>
                        <span className="text-[#005bc1]">{scanPrefs.gmail_limit}</span>
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="500"
                        step="10"
                        value={scanPrefs.gmail_limit}
                        onChange={(e) => setScanPrefs(p => ({ ...p, gmail_limit: parseInt(e.target.value) }))}
                        onMouseUp={(e) => saveScanPrefs({ ...scanPrefs, gmail_limit: parseInt(e.target.value) })}
                        onTouchEnd={(e) => saveScanPrefs({ ...scanPrefs, gmail_limit: parseInt(e.target.value) })}
                        disabled={savingScanPrefs}
                        className="w-full mt-1 accent-[#005bc1]"
                        data-testid="gmail-limit-slider"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider flex justify-between">
                        <span>WhatsApp messages per chat</span>
                        <span className="text-[#005bc1]">{scanPrefs.whatsapp_limit}</span>
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="500"
                        step="10"
                        value={scanPrefs.whatsapp_limit}
                        onChange={(e) => setScanPrefs(p => ({ ...p, whatsapp_limit: parseInt(e.target.value) }))}
                        onMouseUp={(e) => saveScanPrefs({ ...scanPrefs, whatsapp_limit: parseInt(e.target.value) })}
                        onTouchEnd={(e) => saveScanPrefs({ ...scanPrefs, whatsapp_limit: parseInt(e.target.value) })}
                        disabled={savingScanPrefs}
                        className="w-full mt-1 accent-[#005bc1]"
                        data-testid="whatsapp-limit-slider"
                      />
                    </div>
                  </div>
                </div>{/* /Scan limits card */}
              </div>{/* /inner grid */}

              {/* Gmail Privacy Filters (A + B + C) — full-width, modular, self-contained.
                  Adding future filters (D subject, E retroactive cleanup) is
                  done inside GmailFiltersPanel without touching this file. */}
              <GmailFiltersPanel />

              {/* Briefing image style preferences */}
              <div className="p-5 bg-[#f2f4f6] rounded-xl" data-testid="image-style-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Gear size={18} className="text-[#596065]" />
                    <span className="font-semibold text-[#2d3338]">Briefing image style</span>
                  </div>
                  <p className="text-xs text-[#596065] mb-3">
                    Customize the hero image on your dashboard. Time-of-day is automatic (morning, afternoon, evening, night).
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">Aesthetic</label>
                      <select
                        value={imageStyle.prefs.aesthetic || ''}
                        onChange={(e) => saveImageStyle({ prefs: { aesthetic: e.target.value } })}
                        disabled={savingImageStyle}
                        className="w-full mt-1 p-2 rounded-lg border border-[#d7dce0] text-sm bg-white"
                        data-testid="image-style-aesthetic"
                      >
                        <option value="">Auto (varies)</option>
                        {imageStyle.options.aesthetics.map(a => (
                          <option key={a} value={a}>{a[0].toUpperCase() + a.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">Preferred setting</label>
                      <select
                        value={imageStyle.prefs.setting || ''}
                        onChange={(e) => saveImageStyle({ prefs: { setting: e.target.value } })}
                        disabled={savingImageStyle}
                        className="w-full mt-1 p-2 rounded-lg border border-[#d7dce0] text-sm bg-white"
                        data-testid="image-style-setting"
                      >
                        <option value="">Auto</option>
                        {imageStyle.options.settings.map(s => (
                          <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">Person style</label>
                      <select
                        value={imageStyle.prefs.person_style || ''}
                        onChange={(e) => saveImageStyle({ prefs: { person_style: e.target.value } })}
                        disabled={savingImageStyle}
                        className="w-full mt-1 p-2 rounded-lg border border-[#d7dce0] text-sm bg-white"
                        data-testid="image-style-person"
                      >
                        <option value="">Auto</option>
                        {imageStyle.options.person_styles.map(p => (
                          <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">Person appearance (free-text)</label>
                      <input
                        type="text"
                        placeholder="e.g. a warm-toned Indian woman in her 30s"
                        value={imageStyle.person_appearance || ''}
                        onChange={(e) => setImageStyle(prev => ({ ...prev, person_appearance: e.target.value }))}
                        onBlur={() => saveImageStyle({ person_appearance: imageStyle.person_appearance })}
                        className="w-full mt-1 p-2 rounded-lg border border-[#d7dce0] text-sm bg-white"
                        data-testid="image-style-appearance"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-[#596065] uppercase tracking-wider">Avoid</label>
                      <input
                        type="text"
                        placeholder="e.g. no pets, no beaches"
                        value={imageStyle.prefs.avoid || ''}
                        onChange={(e) => setImageStyle(prev => ({ ...prev, prefs: { ...prev.prefs, avoid: e.target.value } }))}
                        onBlur={() => saveImageStyle({ prefs: { avoid: imageStyle.prefs.avoid } })}
                        className="w-full mt-1 p-2 rounded-lg border border-[#d7dce0] text-sm bg-white"
                        data-testid="image-style-avoid"
                      />
                    </div>
                    <p className="text-[10px] text-[#596065] mt-2">
                      Next time you refresh the dashboard, your image will reflect these preferences.
                    </p>
                  </div>
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="connections-grid">
              {/* Scan result banner — rendered here (not at page top) so the
                  user sees it next to the Gmail card where they triggered it. */}
              {scanResult && (
                <div
                  className={`sm:col-span-2 rounded-2xl p-4 flex items-center gap-3 border ${
                    scanResult.success
                      ? 'bg-[#ecfdf5] border-[#bbf7d0]'
                      : 'bg-[#fef2f2] border-[#fecaca]'
                  }`}
                  data-testid="connection-banner"
                >
                  {scanResult.success ? (
                    <CheckCircle size={22} className="text-[#0f6b3a] flex-shrink-0" weight="fill" />
                  ) : (
                    <Warning size={22} className="text-[#991b1b] flex-shrink-0" weight="fill" />
                  )}
                  <p className={`flex-1 font-medium text-sm ${scanResult.success ? 'text-[#064e2f]' : 'text-[#7f1d1d]'}`}>
                    {scanResult.message}
                  </p>
                  {scanResult.success && (
                    <button
                      type="button"
                      onClick={() => navigate('/inbox')}
                      className="text-xs font-semibold text-[#005bc1] hover:underline whitespace-nowrap"
                      data-testid="scan-banner-view-tasks"
                    >
                      View tasks →
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setScanResult(null)}
                    className="p-1 rounded-lg hover:bg-black/5 flex-shrink-0"
                    aria-label="Dismiss"
                    data-testid="scan-banner-dismiss"
                  >
                    <X size={14} className="text-[#596065]" />
                  </button>
                </div>
              )}
              {connections.map(conn => (
                <div 
                  key={conn.id}
                  className={`connection-card ${conn.connected ? 'connected' : ''}`}
                  data-testid={`connection-${conn.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      conn.connected ? 'bg-[#a3f5b8]' : conn.setupRequired ? 'bg-[#fff3cd]' : conn.needsReconnect ? 'bg-[#dbeafe]' : 'bg-[#f2f4f6]'
                    }`}>
                      <conn.icon size={24} weight="duotone" className={conn.connected ? 'text-[#0f6b3a]' : conn.needsReconnect ? 'text-[#1d4ed8]' : 'text-[#596065]'} />
                    </div>
                    {conn.connected && (
                      <span className="tag tag-success">Active</span>
                    )}
                    {conn.needsReconnect && (
                      <span className="tag" style={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}>One Click Setup</span>
                    )}
                    {conn.setupRequired && !conn.comingSoon && (
                      <span className="tag" style={{ backgroundColor: '#fff3cd', color: '#856404' }}>Setup Required</span>
                    )}
                    {conn.comingSoon && (
                      <span className="tag tag-neutral">Coming Soon</span>
                    )}
                  </div>

                  <h3 className="title-md mb-1">{conn.name}</h3>
                  {conn.description && <p className="body-lg text-sm mb-4">{conn.description}</p>}

                  <div className="flex items-center justify-between">
                    {conn.encrypted && (
                      <span className="flex items-center gap-1 text-xs text-[#0f6b3a]">
                        <CheckCircle size={14} weight="fill" />
                        Encrypted
                      </span>
                    )}
                    {conn.readOnly && (
                      <span className="flex items-center gap-1 text-xs text-[#596065]">
                        <Lock size={14} />
                        Read-only access
                      </span>
                    )}
                    {!conn.comingSoon && conn.onConnect && !conn.connected && (
                      <button 
                        onClick={conn.onConnect}
                        className="text-sm font-semibold text-[#005bc1] hover:underline"
                        data-testid={`${conn.id}-connect-btn`}
                      >
                        {conn.needsReconnect ? 'Enable Calendar' : 'Connect'}
                      </button>
                    )}
                    {!conn.comingSoon && conn.connected && conn.onDisconnect && (
                      disconnectConfirm === conn.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-[#596065]">Are you sure?</span>
                          <button 
                            onClick={conn.onDisconnect}
                            className="text-xs font-semibold text-white bg-[#ba1a1a] px-2.5 py-1 rounded-lg hover:bg-[#9c1515] transition-colors"
                            data-testid={`${conn.id}-confirm-disconnect-btn`}
                          >
                            Yes, disconnect
                          </button>
                          <button 
                            onClick={() => setDisconnectConfirm(null)}
                            className="text-xs font-semibold text-[#596065] hover:text-[#2d3338]"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button 
                          onClick={conn.onDisconnect}
                          className="text-sm font-semibold text-[#ba1a1a] hover:underline"
                          data-testid={`${conn.id}-disconnect-btn`}
                        >
                          Disconnect
                        </button>
                      )
                    )}
                    {conn.connected && !conn.onDisconnect && (
                      <span className="text-sm font-semibold text-[#0f6b3a]">Connected</span>
                    )}
                  </div>

                  {conn.connected && conn.onScan && (
                    <button
                      onClick={conn.onScan}
                      disabled={scanning}
                      className="mt-4 btn-primary w-full text-sm disabled:opacity-50"
                      data-testid={`${conn.id}-scan-btn`}
                    >
                      {scanning
                        ? (scanProgress && scanProgress.total > 0
                            ? `Scanning ${scanProgress.stage || '…'} (${scanProgress.current || 0}/${scanProgress.total})`
                            : 'Scanning...')
                        : 'Scan Now'}
                    </button>
                  )}

                  {conn.setupRequired && !conn.comingSoon && (
                    <button className="mt-4 btn-primary w-full text-sm">
                      Link Account
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar - Security Health (sticky so it stays visible as user
              scrolls through the long Filters + Image Style + Scan sections) */}
          <div className="space-y-6 lg:sticky lg:top-8 lg:self-start lg:max-h-[calc(100vh-4rem)] lg:overflow-y-auto">
            <div className="stat-card">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#0f6b3a] animate-pulse"></div>
                <span className="label-md">Security Health</span>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[#2d3338]">Vault Integrity</span>
                  <span className="font-bold text-[#2d3338]">98%</span>
                </div>
                <div className="h-2 bg-[#f2f4f6] rounded-full">
                  <div className="h-full w-[98%] bg-[#005bc1] rounded-full"></div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Key size={20} className="text-[#005bc1] flex-shrink-0 mt-0.5" weight="duotone" />
                  <div>
                    <p className="font-semibold text-sm text-[#2d3338]">Encryption Key Rotated</p>
                    <p className="text-xs text-[#596065]">Your personal keys were updated 4 hours ago for your safety.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <ClockCounterClockwise size={20} className="text-[#596065] flex-shrink-0 mt-0.5" weight="duotone" />
                  <div>
                    <p className="font-semibold text-sm text-[#2d3338]">Audit Log Review</p>
                    <p className="text-xs text-[#596065]">32 automated access points verified by Zero-Trust protocol.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Fingerprint size={20} className="text-[#596065] flex-shrink-0 mt-0.5" weight="duotone" />
                  <div>
                    <p className="font-semibold text-sm text-[#2d3338]">Biometric Auth</p>
                    <p className="text-xs text-[#596065]">Required for financial data access: Enabled.</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-[#acb3b8] mt-6">
                LifeAdmin AI uses end-to-end asymmetric encryption. Your data is never sold or used for training models outside your private instance.
              </p>

              <button className="mt-4 btn-secondary w-full text-sm flex items-center justify-center gap-2">
                <ArrowSquareOut size={16} />
                Export Compliance Report
              </button>
            </div>

            {/* Advanced Protection Card */}
            <div className="stat-card-dark overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#2d3338] to-transparent rounded-bl-full opacity-50"></div>
              <div className="relative text-center">
                <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck size={32} className="text-white" weight="duotone" />
                </div>
                <p className="font-semibold text-white">Advanced Threat Protection</p>
                <p className="text-sm text-[#acb3b8] mt-1">currently active across all data channels.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp QR Code Modal */}
      {showWaQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="whatsapp-qr-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={() => { setShowWaQr(false); setWaPolling(false); }} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#f2f4f6]">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-[#dcfce7] flex items-center justify-center">
                  <WhatsappLogo size={20} className="text-[#25d366]" weight="fill" />
                </div>
                <div>
                  <p className="font-bold text-sm text-[#2d3338]">Connect WhatsApp</p>
                  <p className="text-[10px] text-[#acb3b8]">Scan QR code with your phone</p>
                </div>
              </div>
              <button onClick={() => { setShowWaQr(false); setWaPolling(false); }} className="p-1.5 rounded-lg hover:bg-[#f2f4f6]">
                <X size={18} className="text-[#596065]" />
              </button>
            </div>
            <div className="p-6 flex flex-col items-center">
              {waQr ? (
                <>
                  <img src={waQr} alt="WhatsApp QR Code" className="w-64 h-64 rounded-xl" data-testid="whatsapp-qr-image" />
                  <p className="text-xs text-[#596065] mt-4 text-center">
                    Open WhatsApp on your phone → Settings → Linked Devices → Link a Device
                  </p>
                </>
              ) : (
                <div className="flex flex-col items-center py-8 gap-3">
                  <SpinnerGap size={32} className="text-[#25d366] animate-spin" />
                  <p className="text-sm text-[#596065]">Generating QR code...</p>
                </div>
              )}
              {waPolling && waQr && (
                <div className="flex items-center gap-2 mt-3 text-[10px] text-[#acb3b8]">
                  <SpinnerGap size={12} className="animate-spin" />
                  Waiting for connection...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
